import { useQueryCache } from '@pinia/colada';
import { acceptHMRUpdate, defineStore } from 'pinia';

import { FileDto } from '@tallo/file-manager';

import { portfolioQueryKey } from '../queries/portfolio.query';
import propertyService from '../services/property.service';
import { usePropertyStore } from './property.store';

export const usePropertyImagesStore = defineStore({
  id: 'propertyImages',
  state: (): {
    isSaving: boolean;
    isOrderChanged: boolean;
    images: FileDto[];
  } => ({
    isSaving: false,
    isOrderChanged: false,
    images: [],
  }),
  actions: {
    init() {
      const propertyStore = usePropertyStore();

      this.images = [...propertyStore.images];
    },

    async deleteImage(imageId: string) {
      const { deleteImage } = usePropertyStore();

      const imageIndex = this.images.findIndex(({ id }) => id === imageId);

      if (imageIndex > -1) {
        this.images.splice(imageIndex, 1);
      }

      this.isOrderChanged = true;

      await deleteImage(imageId);
    },

    async addImage(file: File) {
      const { property, setImages } = usePropertyStore();
      const propertyImages = property?.images || [];
      const { data } = await propertyService.uploadImage(property!.id, file);

      propertyImages.push(data);
      this.images.push(data);
      this.isOrderChanged = true;

      setImages(propertyImages);
    },

    async updateImagesOrder() {
      if (!this.isOrderChanged) {
        return;
      }

      this.isSaving = true;

      const queryCache = useQueryCache();
      const { property, setImages } = usePropertyStore();
      const { data } = await propertyService.updateImagesOrder(property!.id, this.images);

      this.isSaving = false;
      this.images = [...data];

      setImages([...data]);
      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    setReorderedImages(images: FileDto[]) {
      this.isOrderChanged = true;
      this.images = images;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePropertyImagesStore, import.meta.hot));
}
